import { useLayoutEffect, type ReactNode } from 'react';
import { usePanelRef } from 'react-resizable-panels';
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from '@/shared/ui/resizable';

type ChatLayoutProps = {
  /** Thread content — Thread.tsx, which owns the transcript, composer, and empty state internally. */
  content: ReactNode;
  sidebar?: ReactNode;
  /**
   * Claude-Artifacts-style side panel (ArtifactPanel). Always rendered inside
   * its own ResizablePanel — open/closed is driven by `artifactPanelOpen`
   * collapsing that panel in place, NOT by conditionally changing which
   * element wraps `content`. Swapping content's wrapper type (e.g. between
   * ResizablePanelGroup and a plain div) forces React to unmount and remount
   * the entire subtree at that position on every open/close, which tore down
   * every thread's live runtime connection underneath `content` and reset
   * ArtifactWatcher's `seenCallIds` ref — the reset then replayed every
   * already-seen `generative-ui-artifact` call through `pushRevision`, which
   * unconditionally reopens the panel, making close a no-op.
   */
  artifactPanel?: ReactNode;
  artifactPanelOpen?: boolean;
};

export function ChatLayout({ content, sidebar, artifactPanel, artifactPanelOpen = false }: ChatLayoutProps) {
  const artifactPanelRef = usePanelRef();

  useLayoutEffect(() => {
    const panel = artifactPanelRef.current;
    if (!panel) return;
    if (artifactPanelOpen && panel.isCollapsed()) panel.expand();
    else if (!artifactPanelOpen && !panel.isCollapsed()) panel.collapse();
  }, [artifactPanelOpen, artifactPanelRef]);

  return (
    <section
      aria-label="Chat workspace"
      className="flex flex-1 min-h-0 overflow-hidden max-[640px]:flex-col"
    >
      {sidebar ? (
        <aside className="w-[280px] shrink-0 overflow-y-auto border-r border-border max-[640px]:max-h-[40%] max-[640px]:w-full max-[640px]:border-r-0 max-[640px]:border-b">
          {sidebar}
        </aside>
      ) : null}
      <ResizablePanelGroup orientation="horizontal" className="min-w-0 flex-1">
        <ResizablePanel defaultSize="65%" minSize="50%" className="min-w-0">
          <div className="flex h-full min-w-0 flex-col overflow-hidden">{content}</div>
        </ResizablePanel>
        <ResizableHandle withHandle aria-label="Resize artifact panel" className="cursor-col-resize" />
        <ResizablePanel
          panelRef={artifactPanelRef}
          collapsible
          defaultSize={artifactPanelOpen ? '35%' : 0}
          minSize="25%"
          maxSize="50%"
          className="min-w-0"
        >
          {artifactPanel}
        </ResizablePanel>
      </ResizablePanelGroup>
    </section>
  );
}
